import * as Sentry from '@sentry/react'
import { isRouteErrorResponse } from 'react-router-dom'

/**
 * DBS-350: what this build is, stamped by scripts/build.sh as fe-<portal>@<commit>[-dirty]; 'development' under npm start.
 * Sentry events carry it as their release, and version.json in the build holds the same value.
 */
export const RELEASE: string = process.env.REACT_APP_RELEASE || 'development'

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Sign-in errors the portal recovers from on its own; the api leaves them out of Sentry too (DBS-349) */
const EXPECTED_SIGN_IN_ERROR_TYPES = ['errProfileJwtExpired', 'jwtExpired', 'errProfileNotFound']

export const sentryOptions = ({ dsn, apiUrl, isLocal }: { dsn: string | undefined, apiUrl: string, isLocal: boolean }): Sentry.BrowserOptions => ({
  dsn,
  release: RELEASE === 'development' ? undefined : RELEASE,
  environment: isLocal ? 'development' : 'production',
  // DBS-349: Auth0's "Login required" means the Auth0 session lapsed; the portal sends the user to log in again
  ignoreErrors: ['Login required'],
  integrations: [
    // DBS-350: no performance spans (tracesSampleRate is unset), only the trace headers on api calls, so a portal
    // error and the api error behind it share one trace in Sentry
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracePropagationTargets: [new RegExp(`^${escapeRegExp(apiUrl)}`)],
  // Session Replay
  // DBS-349: no routine session replays; the free plan's monthly replay allowance went in days (5 Oct 2026). Keep it for sessions with an error.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
})

/**
 * DBS-350: whether a route error (a loader, action or render failure shown on ErrorPage) goes to Sentry, and with which
 * tags. Unknown URLs and the sign-in errors the portal recovers from are left out.
 */
export type RouteErrorReport = { tags: Record<string, string>, contexts?: Record<string, Record<string, unknown>> }

export const routeErrorReport = (error: unknown): RouteErrorReport | undefined => {
  if (isRouteErrorResponse(error)) {
    return error.status === 404 ? undefined : { tags: { route_error: 'response', http_status: String(error.status) } }
  }
  const response = (error as { response?: { status?: number, data?: { error?: { id?: unknown, type?: unknown, message?: unknown, sentry?: unknown } } } } | undefined)?.response
  const body = response?.data?.error
  const type = typeof body?.type === 'string' ? body.type : undefined
  if (type !== undefined && EXPECTED_SIGN_IN_ERROR_TYPES.includes(type)) return undefined

  const tags: Record<string, string> = { route_error: response ? 'api' : 'exception' }
  if (response?.status != null) tags.http_status = String(response.status)
  if (type) tags.api_error_type = type
  if (typeof body?.id === 'string') tags.api_error_id = body.id
  if (typeof body?.sentry === 'string') tags.api_sentry_event = body.sentry
  // DBS-354: a request-validation refusal names the field and the rule it broke, never the value sent, so it goes with
  // the event. Other api messages can quote what the user typed (e.g. the DBS rule messages), so they stay out.
  if (type === 'svrRequestValidation' && typeof body?.message === 'string') {
    return { tags, contexts: { api_error: { message: body.message.slice(0, 1000) } } }
  }
  return { tags }
}

export const reportRouteError = (error: unknown): void => {
  const report = routeErrorReport(error)
  if (!report) return
  if (isRouteErrorResponse(error)) {
    Sentry.captureMessage(`Route error response ${error.status}: ${error.statusText}`, { level: 'error', tags: report.tags })
  } else {
    Sentry.captureException(error, { tags: report.tags, contexts: report.contexts })
  }
}
